import type { PosSettings, ReceiptData } from '@/types';
import { getCountry, isGcc, isSaudiArabia } from '@/data/locales';

export function isZatcaCreditNote(inv?: ReceiptData | null): boolean {
  if (!inv) return false;
  const status = String(inv.status || '').toLowerCase();
  return status.includes('return') || (inv.net_total ?? 0) < 0;
}

/** ZATCA Fatoora mandatory receipt fields for simplified B2C invoice. */
export function getZatcaReceiptSummary(
  inv: ReceiptData,
  posSettings?: PosSettings | null,
): {
  sellerVat: string;
  invoiceDateTime: string;
  totalWithVat: number;
  vatAmount: number;
  documentTitleKey: 'receipt.simplifiedTaxInvoice' | 'receipt.creditNote';
} | null {
  if (!shouldShowZatcaBlock(inv, posSettings)) return null;
  const d = new Date(inv.creation || inv.invoice_date || Date.now());
  const sellerVat = posSettings?.zatca_vat_number || posSettings?.tax_registration_id || '';
  return {
    sellerVat,
    invoiceDateTime: d.toLocaleString('ar-SA'),
    totalWithVat: inv.net_total ?? 0,
    vatAmount: inv.tax_amount ?? 0,
    documentTitleKey: isZatcaCreditNote(inv) ? 'receipt.creditNote' : 'receipt.simplifiedTaxInvoice',
  };
}

export function getDisplayCompanyName(
  inv?: ReceiptData | null,
  posSettings?: PosSettings | null,
): string {
  const arName = inv?.company_name_ar || posSettings?.company_name_ar || posSettings?.zatca_company_name_ar;
  const enName = inv?.company_name || '';
  const lang = posSettings?.ui_language || 'en';
  const country = posSettings?.country || 'PK';
  if (arName && (lang === 'ar' || isGcc(country))) {
    return arName;
  }
  return enName;
}

export function getSellerTaxLines(posSettings?: PosSettings | null): { label: string; value: string }[] {
  if (!posSettings) return [];
  const country = posSettings.country || 'PK';
  const isPk = country === 'PK';
  const lines: { label: string; value: string }[] = [];
  if (isPk) {
    if (posSettings.fbr_ntn) lines.push({ label: 'NTN', value: posSettings.fbr_ntn });
    if (posSettings.fbr_strn) lines.push({ label: 'GST/STRN', value: posSettings.fbr_strn });
  } else if (posSettings.tax_registration_id) {
    const label = getCountry(country).taxIdLabel || (country === 'US' ? 'EIN' : 'VAT');
    lines.push({ label, value: posSettings.tax_registration_id });
  } else if (isSaudiArabia(country) && posSettings.zatca_vat_number) {
    lines.push({ label: 'VAT / TRN', value: posSettings.zatca_vat_number });
  } else if (country === 'AE' && posSettings.uae_trn) {
    lines.push({ label: 'TRN', value: posSettings.uae_trn });
  }
  return lines;
}

export function companyContactParts(
  inv: ReceiptData | null | undefined,
  opts: { show_address?: boolean; show_phone?: boolean; show_email?: boolean },
): string[] {
  const parts: string[] = [];
  if (opts.show_address && inv?.company_address) parts.push(inv.company_address);
  if (opts.show_phone && inv?.company_phone) parts.push(inv.company_phone);
  if (opts.show_email && inv?.company_email) parts.push(inv.company_email);
  return parts;
}

export function shouldShowFbrBlock(inv?: ReceiptData | null, posSettings?: PosSettings | null): boolean {
  if ((posSettings?.country || 'PK') !== 'PK') return false;
  return Boolean(
    inv?.fbr_invoice_number
    || inv?.fbr_status === 'Submitted'
    || inv?.fbr_status === 'Pending'
    || inv?.fbr_qr_code
    || posSettings?.enable_fbr_integration,
  );
}

export function shouldShowZatcaBlock(inv?: ReceiptData | null, posSettings?: PosSettings | null): boolean {
  if (!isSaudiArabia(posSettings?.country)) return false;
  return Boolean(
    inv?.zatca_uuid
    || inv?.zatca_status === 'Submitted'
    || inv?.zatca_status === 'Pending'
    || inv?.zatca_qr_code
    || posSettings?.enable_zatca_integration,
  );
}
